import { useState, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import { Align, Chart, InteractionMode, InteractionAxis, TooltipPositioner } from 'chart.js';
import gettext from '@sdk/lang/gettext';
import palette from '@sdk/theme/colorsLight';
import { ShiftFeedbackRating } from '@sdk/timesheets/types/ShiftFeedback';

export type DataPoint = {
  x: string;
  y: number | null;
  tooltipCaptionLabel?: string;
  tooltipCaptionValue?: string;
  averageValueRounded?: string | null;
  tallies?: {
    sum: number;
    count: number;
    [ShiftFeedbackRating.excellent]: number;
    [ShiftFeedbackRating.great]: number;
    [ShiftFeedbackRating.good]: number;
    [ShiftFeedbackRating.fair]: number;
    [ShiftFeedbackRating.poor]: number;
  };
};

const TOOLTIP_WIDTH = 265;
const TOOLTIP_RIGHT_OFFSET = 20;
const TOOLTIP_LEFT_OFFSET = 0;
const TOOLTIP_TOP_OFFSET = 10;

const additionalScaleXOptions = (data: DataPoint[]) => {
  if (data.length <= 7) {
    return {};
  }

  return {
    autoSkip: false,
    callback: function (value, index, values) {
      // Show labels only for the first, last, and middle items
      if (index === 0 || index === values.length - 1 || index === Math.floor(values.length / 2)) {
        return this.getLabelForValue(value);
      } else {
        return '';
      }
    },
    maxRotation: 0,
    minRotation: 0,
  };
};

export const useLineGraph = (data: DataPoint[], chartsScaleFactor: number) => {
  const [tooltipData, setTooltipData] = useState({
    opacity: 0,
    top: 0,
    left: 0,
    dataPoint: null,
  });

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          data,
          borderColor: palette.primary,
          borderWidth: 2 * chartsScaleFactor,
          pointRadius: (data.filter(({ y }) => y !== null).length > 1 ? 1 : 3) * chartsScaleFactor,
          pointHoverRadius: 4 * chartsScaleFactor,
          pointBorderColor: 'transparent',
          pointBackgroundColor: palette.primary,
          pointHoverBorderColor: palette.primary,
          pointHoverBackgroundColor: palette.white,
          pointHoverBorderWidth: 2 * chartsScaleFactor,
        },
      ],
    }),
    [data, chartsScaleFactor]
  );

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      spanGaps: true,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: gettext('Average rating over time'),
          align: 'start' as Align,
          color: palette.gray,
          padding: {
            top: 10 * chartsScaleFactor,
            bottom: 15 * chartsScaleFactor,
          },
          font: {
            size: 12 * chartsScaleFactor,
          },
        },
        tooltip: {
          enabled: false,
          position: 'nearest' as TooltipPositioner,
          external: ({ chart }: { chart: Chart }) => {
            const tooltipModel = chart.tooltip;

            if (tooltipModel.opacity === 0) {
              if (tooltipData.opacity !== 0) {
                setTooltipData((prev) => ({ ...prev, opacity: 0 }));
              }

              return;
            }

            const tooltipLeft =
              tooltipModel.caretX < chart.width / 2
                ? tooltipModel.caretX + TOOLTIP_RIGHT_OFFSET
                : tooltipModel.caretX + TOOLTIP_LEFT_OFFSET - TOOLTIP_WIDTH;

            const newTooltipData = {
              opacity: 1,
              left: tooltipLeft,
              top: TOOLTIP_TOP_OFFSET,
              dataPoint:
                tooltipModel.dataPoints[0].dataset.data[tooltipModel.dataPoints[0].dataIndex],
            };

            if (!isEqual(tooltipData, newTooltipData)) {
              setTooltipData(newTooltipData);
            }
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: palette.primaryText,
            font: {
              size: 12 * chartsScaleFactor,
            },
            ...additionalScaleXOptions(data),
          },
          grid: {
            color: palette.separatorOnWhite,
          },
        },
        y: {
          beginAtZero: true,
          suggestedMax: 5,
          ticks: {
            color: palette.iconGray,
            font: {
              size: 12 * chartsScaleFactor,
            },
            callback: function (value) {
              const label = this.getLabelForValue(value);
              if (label === '0') {
                return '';
              }

              return label;
            },
          },
          grid: {
            color: palette.separatorOnWhite,
          },
        },
      },
      interaction: {
        mode: 'nearestExcludingZero' as InteractionMode,
        axis: 'x' as InteractionAxis,
        intersect: false,
      },
    }),
    [tooltipData, setTooltipData, data, chartsScaleFactor]
  );

  const plugins = useMemo(
    () => [
      {
        id: 'vertical line',
        afterDraw: (chart: Chart) => {
          const x = chart.tooltip.caretX;
          const yAxis = chart.scales.y;
          const ctx = chart.ctx;
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(x, yAxis.top);
          ctx.lineTo(x, yAxis.bottom);
          ctx.lineWidth = 1;
          ctx.strokeStyle = palette.iconGray;
          ctx.globalAlpha = chart.tooltip.opacity;
          ctx.stroke();
          ctx.restore();
        },
      },
    ],
    []
  );

  return {
    chartData,
    options,
    plugins,
    tooltipData,
  };
};
